import { useEffect, useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type MerchantRole = 'super_admin' | 'manager' | 'view_only' | null;

interface MerchantRoleState {
  role: MerchantRole;
  loading: boolean;
  merchantId: string | null;
  isSuperAdmin: boolean;
  isManager: boolean;
  isViewOnly: boolean;
  canManagePayments: boolean;
  canManageRefunds: boolean;
  canManageProducts: boolean;
  canViewFees: boolean;
  canAccessSettings: boolean;
  canManageTeam: boolean;
}

export function useMerchantRole(): MerchantRoleState {
  const { user, loading: authLoading } = useAuth();
  const [role, setRole] = useState<MerchantRole>(null);
  const [loading, setLoading] = useState(true);
  const [merchantId, setMerchantId] = useState<string | null>(null);
  const lastUserId = useRef<string | null>(null);

  useEffect(() => {
    // Don't do anything while auth is still loading
    if (authLoading) {
      setLoading(true);
      return;
    }

    // If no user, reset state and stop loading
    if (!user) {
      setRole(null);
      setMerchantId(null);
      setLoading(false);
      lastUserId.current = null;
      return;
    }

    // Skip if we already fetched for this user
    if (lastUserId.current === user.id) {
      return;
    }

    const fetchRole = async () => {
      setLoading(true);
      lastUserId.current = user.id;

      try {
        const { data: merchantUser, error } = await supabase
          .from('merchant_users')
          .select('merchant_id, role')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error || !merchantUser) {
          console.log('No merchant association found for user');
          setRole(null);
          setMerchantId(null);
        } else {
          setRole(merchantUser.role as MerchantRole);
          setMerchantId(merchantUser.merchant_id);
        }
      } catch (error) {
        console.error('Error fetching merchant role:', error);
        setRole(null);
        setMerchantId(null);
      } finally {
        setLoading(false);
      }
    };

    fetchRole();
  }, [user, authLoading]);

  const isSuperAdmin = role === 'super_admin';
  const isManager = role === 'manager';
  const isViewOnly = role === 'view_only';

  return {
    role,
    loading,
    merchantId,
    isSuperAdmin,
    isManager,
    isViewOnly,
    canManagePayments: isSuperAdmin || isManager,
    canManageRefunds: isSuperAdmin || isManager,
    canManageProducts: isSuperAdmin || isManager,
    canViewFees: isSuperAdmin,
    canAccessSettings: isSuperAdmin,
    canManageTeam: isSuperAdmin,
  };
};
